import React, {ReactNode, useMemo} from 'react';
import {Box, Dialog, DialogActions, DialogContent, IconButton} from '@mui/material';
import {Formik, useFormikContext} from 'formik';
import moment from 'moment';
import {useIntl} from 'react-intl';
import {useMediaQuery} from 'react-responsive';
import * as Yup from 'yup';
import {SVGS} from '../../../assets/images';
import {ActionCancelButtonsArea} from '../../../components/shared/action-cancel-buttons-area';
import {FormCheckbox} from '../../../components/shared/form-controls/form-checkbox';
import FormAutocomplete from '../../../components/shared/form-controls/form-autocomplete';
import {AutoCompleteItemValue} from '../../../components/shared/form-controls/form-autocomplete/types';
import {DropdownField} from '../../../components/shared/nextgen/dropdown-field';
import FormDateSelector from '../../../components/shared/nextgen/form/form-date-selector';
import {FormNumberInput} from '../../../components/shared/nextgen/form/form-number-input';
import {FormTextarea} from '../../../components/shared/nextgen/form/form-textarea';
import {Lx} from '../../../i18n/consts';
import {
    buildManualImportRowValues,
    createManualImportRow,
    ImportEntityOption,
    ManualImportRow,
    ManualImportRowKind,
    ManualImportRowOptions,
    MANUAL_ROW_HOURS_STEP,
    MANUAL_ROW_MAX_HOURS,
    MANUAL_ROW_MIN_HOURS,
    parseManualRowHours,
} from '../utils/manualRows';
import styles from '../time-tracking-import.module.scss';

interface EntityOptionItem {
    id: string;
    name: ReactNode;
    entityName: string;
    isPending: boolean;
    isDefault: boolean;
}

interface TimeTypeOptionItem extends EntityOptionItem {
    kind: ManualImportRowKind;
}

interface EmployeeOptionItem {
    id: string;
    name: ReactNode;
}

interface ManualRowFormValues {
    employee: EmployeeOptionItem | null;
    date: string;
    timeType: TimeTypeOptionItem | null;
    project: EntityOptionItem | null;
    hours: number;
    tags: AutoCompleteItemValue[];
    isOvertime: boolean;
    comment: string;
}

interface TimeTrackingImportManualRowDialogProps {
    open: boolean;
    /** Filled when an already added row is being edited, otherwise a new one is added. */
    editedRow: ManualImportRow | null;
    employeeValue: string;
    employeeValues: string[];
    defaultDate: string;
    options: ManualImportRowOptions;
    minDate: moment.Moment;
    maxDate: moment.Moment;
    onSave: (manualRow: ManualImportRow) => void;
    onClose: () => void;
}

const DEFAULT_MANUAL_ROW_HOURS = 8;

const getOptionId = (prefix: string, entityName: string) => `${prefix}::${entityName}`;

const buildEntityOptions = (
    prefix: string,
    options: ImportEntityOption[],
    pendingLabel: string
): EntityOptionItem[] => options.map((option) => ({
    id: getOptionId(prefix, option.name),
    entityName: option.name,
    isPending: option.isPending,
    isDefault: option.isDefault,
    name: option.isPending
        ? (
            <span className={styles.manualRowOption}>
                <span className={styles.manualRowOptionName}>{option.name}</span>
                <span className={styles.manualRowOptionPendingMark}>{pendingLabel}</span>
            </span>
        )
        : option.name,
}));

// A value of an already added row may have disappeared from the dictionaries (say, the entity was
// renamed in the mapping step). It is shown as a separate option so that editing a row does not
// lose the value silently.
const withFallbackOption = <TOption extends EntityOptionItem>(
    options: TOption[],
    entityName: string,
    createFallback: (entityName: string) => TOption
): TOption[] => !entityName || options.some((option) => option.entityName === entityName)
        ? options
        : [...options, createFallback(entityName)];

const findEntityOption = <TOption extends EntityOptionItem>(
    options: TOption[],
    entityName: string
): TOption | null => options.find((option) => option.entityName === entityName) ?? null;

const ManualRowFormEffects: React.FC = () => {
    const {values, setFieldValue} = useFormikContext<ManualRowFormValues>();
    const isAbsence = values.timeType?.kind === 'absence';

    React.useEffect(() => {
        if (!isAbsence) {
            return;
        }

        // An absence goes without a project and tags: the server ignores absence tags with a warning.
        if (values.project) {
            void setFieldValue('project', null, false);
        }

        if (values.tags.length > 0) {
            void setFieldValue('tags', [], false);
        }

        if (values.isOvertime) {
            void setFieldValue('isOvertime', false, false);
        }
    }, [isAbsence, setFieldValue, values.isOvertime, values.project, values.tags.length]);

    return null;
};

export default function TimeTrackingImportManualRowDialog({
    open,
    editedRow,
    employeeValue,
    employeeValues,
    defaultDate,
    options,
    minDate,
    maxDate,
    onSave,
    onClose,
}: TimeTrackingImportManualRowDialogProps) {
    const intl = useIntl();
    const isMobile = useMediaQuery({maxWidth: 1000});
    const pendingLabel = intl.formatMessage({id: Lx.TimeTrackingImport.PREVIEW_NEW_ENTITY_TAG});
    const employeeOptions = useMemo<EmployeeOptionItem[]>(
        () => employeeValues.map((value) => ({id: value, name: value})),
        [employeeValues]
    );
    const projectOptions = useMemo(() => withFallbackOption(
        buildEntityOptions('project', options.projects, pendingLabel),
        editedRow?.kind === 'shift' ? editedRow.projectOrAbsence : '',
        (entityName) => ({
            id: getOptionId('project', entityName),
            name: entityName,
            entityName,
            isPending: true,
            isDefault: false,
        })
    ), [editedRow, options.projects, pendingLabel]);
    const timeTypeOptions = useMemo(() => {
        const shiftOptions = buildEntityOptions('shift', options.shiftTypes, pendingLabel)
            .map((option): TimeTypeOptionItem => ({...option, kind: 'shift'}));
        const absenceOptions = buildEntityOptions('absence', options.absenceTypes, pendingLabel)
            .map((option): TimeTypeOptionItem => ({...option, kind: 'absence'}));
        const editedEntityName = editedRow?.kind === 'absence'
            ? editedRow.projectOrAbsence
            : editedRow?.type ?? '';

        return withFallbackOption(
            [...shiftOptions, ...absenceOptions],
            editedEntityName,
            (entityName) => ({
                id: getOptionId(editedRow?.kind ?? 'shift', entityName),
                name: entityName,
                entityName,
                isPending: true,
                isDefault: false,
                kind: editedRow?.kind ?? 'shift',
            })
        );
    }, [editedRow, options.absenceTypes, options.shiftTypes, pendingLabel]);
    // A row without a shift type (an empty column in the file — the server substitutes the default
    // type and warns about it) must open with that same type, not with an empty required field.
    const defaultShiftTypeOption = useMemo(() => {
        const shiftOptions = timeTypeOptions.filter((option) => option.kind === 'shift');

        return shiftOptions.find((option) => option.isDefault)
            ?? shiftOptions.find((option) => !option.isPending)
            ?? shiftOptions[0]
            ?? null;
    }, [timeTypeOptions]);
    const tagOptions = useMemo<AutoCompleteItemValue[]>(
        () => options.tags.map((option, index) => ({
            id: index + 1,
            name: option.name,
            isDefault: false,
        })),
        [options.tags]
    );
    const initialValues = useMemo<ManualRowFormValues>(() => {
        const selectedEmployee = employeeOptions.find((option) => option.id === employeeValue)
            ?? employeeOptions[0]
            ?? null;

        if (!editedRow) {
            return {
                employee: selectedEmployee,
                date: defaultDate,
                timeType: defaultShiftTypeOption,
                project: null,
                hours: DEFAULT_MANUAL_ROW_HOURS,
                tags: [],
                isOvertime: false,
                comment: '',
            };
        }

        const editedHours = parseManualRowHours(editedRow.hours);

        return {
            employee: employeeOptions.find((option) => option.id === editedRow.employeeValue)
                ?? selectedEmployee,
            date: editedRow.date || defaultDate,
            timeType: findEntityOption(
                timeTypeOptions,
                editedRow.kind === 'absence' ? editedRow.projectOrAbsence : editedRow.type
            ) ?? (editedRow.kind === 'absence' ? null : defaultShiftTypeOption),
            project: editedRow.kind === 'absence'
                ? null
                : findEntityOption(projectOptions, editedRow.projectOrAbsence),
            hours: Number.isNaN(editedHours) ? DEFAULT_MANUAL_ROW_HOURS : editedHours,
            tags: editedRow.tags
                .map((tagName) => tagOptions.find((option) => option.name === tagName)
                    ?? {id: -1, name: tagName, isDefault: false})
                .filter((tag) => Boolean(tag.name)),
            isOvertime: editedRow.isOvertime,
            comment: editedRow.note,
        };
    }, [
        defaultDate,
        defaultShiftTypeOption,
        editedRow,
        employeeOptions,
        employeeValue,
        projectOptions,
        tagOptions,
        timeTypeOptions,
    ]);
    const validationSchema = useMemo(() => Yup.object().shape({
        employee: Yup.mixed<EmployeeOptionItem>().nullable().required(
            intl.formatMessage({id: Lx.Form.REQUIRED})
        ),
        date: Yup.string()
            .required(intl.formatMessage({id: Lx.Form.REQUIRED}))
            .test(
                'manual-row-allowed-date',
                intl.formatMessage({id: Lx.TimeTrackingImport.MANUAL_ROW_DATE_OUT_OF_RANGE}),
                (value) => {
                    const parsedDate = moment(value, 'YYYY-MM-DD', true);

                    return parsedDate.isValid()
                        && !parsedDate.isBefore(minDate, 'day')
                        && !parsedDate.isAfter(maxDate, 'day');
                }
            ),
        timeType: Yup.mixed<TimeTypeOptionItem>().nullable().required(
            intl.formatMessage({id: Lx.Form.REQUIRED})
        ),
        project: Yup.mixed<EntityOptionItem>().nullable().test(
            'manual-row-project-required',
            intl.formatMessage({id: Lx.Form.REQUIRED}),
            (value, context) =>
                (context.parent as ManualRowFormValues).timeType?.kind === 'absence' || Boolean(value)
        ),
        hours: Yup.number()
            .required(intl.formatMessage({id: Lx.Form.REQUIRED}))
            .min(MANUAL_ROW_MIN_HOURS)
            .max(MANUAL_ROW_MAX_HOURS),
    }), [intl, maxDate, minDate]);

    if (!open) {
        return null;
    }

    const handleSubmit = (values: ManualRowFormValues) => {
        if (!values.employee || !values.timeType) {
            return;
        }

        const rowValues = buildManualImportRowValues({
            employeeValue: values.employee.id,
            kind: values.timeType.kind,
            date: values.date,
            hours: String(values.hours),
            projectName: values.project?.entityName ?? '',
            typeName: values.timeType.entityName,
            note: values.comment.trim(),
            tags: values.tags.map((tag) => tag.name),
            isOvertime: values.isOvertime,
        });

        onSave(editedRow
            ? {...rowValues, id: editedRow.id}
            : createManualImportRow(rowValues));
    };

    return (
        <Dialog
            open={open}
            fullScreen={isMobile}
            onClose={onClose}
            aria-labelledby='manual-row-dialog-title'
            PaperProps={{className: styles.manualRowDialogPaper}}
        >
            <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                validateOnBlur
                validateOnChange
                onSubmit={handleSubmit}
            >
                {({values, submitForm}) => (
                    <>
                        <ManualRowFormEffects />
                        <div className={styles.manualRowDialogHeader}>
                            <h3 id='manual-row-dialog-title'>
                                {intl.formatMessage({
                                    id: editedRow
                                        ? Lx.TimeTrackingImport.MANUAL_ROW_DIALOG_EDIT_TITLE
                                        : Lx.TimeTrackingImport.MANUAL_ROW_DIALOG_ADD_TITLE,
                                })}
                            </h3>
                            <p>{intl.formatMessage({id: Lx.TimeTrackingImport.MANUAL_ROW_DIALOG_DESCRIPTION})}</p>
                            {isMobile ? null : (
                                <IconButton
                                    aria-label={intl.formatMessage({id: Lx.General.CANCEL})}
                                    onClick={onClose}
                                    className={styles.manualRowDialogClose}
                                >
                                    <SVGS.CrossIcon />
                                </IconButton>
                            )}
                        </div>
                        <DialogContent className={styles.manualRowDialogContent}>
                            <DropdownField
                                label={intl.formatMessage({id: Lx.General.EMPLOYEE})}
                                name='employee'
                                items={employeeOptions}
                                placeholder={intl.formatMessage({id: Lx.General.CHOOSE_EMPLOYEE})}
                                description={intl.formatMessage({
                                    id: Lx.TimeTrackingImport.MANUAL_ROW_EMPLOYEE_HINT,
                                })}
                                disabled={employeeOptions.length < 2}
                                required
                            />
                            <Box
                                display='flex'
                                flexDirection={isMobile ? 'column' : 'row'}
                                alignItems={isMobile ? 'stretch' : 'flex-start'}
                                gap={3}
                                sx={{'& > *': {flex: '1 1 0', minWidth: 0}}}
                            >
                                <FormDateSelector
                                    label={intl.formatMessage({id: Lx.Schedule.TIMESHIFT_DATE})}
                                    name='date'
                                    minDate={minDate}
                                    maxDate={maxDate}
                                    required
                                />
                                <DropdownField
                                    label={intl.formatMessage({id: Lx.General.SHIFT_TYPE})}
                                    name='timeType'
                                    items={timeTypeOptions}
                                    placeholder={intl.formatMessage({id: Lx.General.SHIFT_TYPE})}
                                    required
                                />
                            </Box>
                            <Box
                                display='flex'
                                flexDirection={isMobile ? 'column' : 'row'}
                                alignItems={isMobile ? 'stretch' : 'flex-start'}
                                gap={3}
                                sx={{'& > *': {flex: '1 1 0', minWidth: 0}}}
                            >
                                <DropdownField
                                    label={intl.formatMessage({id: Lx.General.PROJECT})}
                                    name='project'
                                    items={projectOptions}
                                    placeholder={intl.formatMessage({id: Lx.General.PROJECT})}
                                    disabled={values.timeType?.kind === 'absence'}
                                    required={values.timeType?.kind !== 'absence'}
                                />
                                <FormNumberInput
                                    label={intl.formatMessage({id: Lx.Schedule.TIMESHIFT_HOURS})}
                                    name='hours'
                                    placeholder={intl.formatMessage({id: Lx.Schedule.TIMESHIFT_HOURS})}
                                    min={MANUAL_ROW_MIN_HOURS}
                                    max={MANUAL_ROW_MAX_HOURS}
                                    step={MANUAL_ROW_HOURS_STEP}
                                    required
                                />
                            </Box>
                            {values.timeType?.kind === 'absence' ? null : (
                                <>
                                    <FormAutocomplete
                                        label={intl.formatMessage({id: Lx.General.TAGS})}
                                        placeHolder={intl.formatMessage({id: Lx.General.START_TYPING})}
                                        name='tags'
                                        items={tagOptions}
                                        selectedItems={values.tags}
                                    />
                                    <FormCheckbox
                                        name='isOvertime'
                                        label={intl.formatMessage({id: Lx.Schedule.IS_OVERTIME})}
                                        style={{marginBottom: 0}}
                                    />
                                </>
                            )}
                            <FormTextarea
                                label={intl.formatMessage({id: Lx.Schedule.TIMESHIFT_NOTE})}
                                name='comment'
                                placeholder={intl.formatMessage({id: Lx.Schedule.TIMESHIFT_NOTE})}
                                resize='vertical'
                                multiple
                            />
                        </DialogContent>
                        <DialogActions className={styles.manualRowDialogActions}>
                            <ActionCancelButtonsArea
                                idActionLocalization={Lx.General.SAVE}
                                onCancel={onClose}
                                onAction={() => void submitForm()}
                            />
                        </DialogActions>
                    </>
                )}
            </Formik>
        </Dialog>
    );
}
